class TfVariantPicker extends HTMLElement {
  constructor() {
    super();
    this.variants = [];
    this.extra = {};
    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
  }

  connectedCallback() {
    this.inventoryMap = this._readJSON('script[data-inventory-map]') || {};
    this.variants = this._readJSON('script[data-variants]') || [];
    this.extra = this._readJSON('script[data-variant-extra]') || {};

    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);

    const initial =
      this._getVariantFromUrl() ||
      this._getCurrentVariantFromHidden() ||
      this.variants.find(v => v.available) ||
      this.variants[0] ||
      null;

    if (initial) {
      this._applyVariantToUI(initial);
      this._applyVariant(initial);

      // chống bị CSS/JS khác ghi đè ngay sau load
      requestAnimationFrame(() => this._applyVariant(initial));
      setTimeout(() => this._applyVariant(initial), 150);
    }

    // sync theo option state (để các click/change vẫn chạy)
    this._syncVariant();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
  }

  /* ---------------- ROOT / DOM HELPERS ---------------- */

  _getRoot() {
    // Ưu tiên wrapper product nếu bạn có (bạn đang dùng cho buy-actions)
    return this.closest('[data-product-root]') || this.closest('.shopify-section') || this.closest('main') || document;
  }

  _readJSON(sel) {
    const el = this.querySelector(sel);
    if (!el) return null;
    try { return JSON.parse(el.textContent || ''); } catch { return null; }
  }

  _show(el, fallbackDisplay = 'block') {
    if (!el) return;

    el.hidden = false;
    el.classList.remove('d-none', 'hidden', 'is-hidden', 'visually-hidden');

    // clear inline display
    if (el.style) el.style.display = '';

    // if still hidden by CSS rules -> force display
    const cs = window.getComputedStyle(el);
    if (cs && cs.display === 'none') {
      el.style.display = fallbackDisplay;
    }
  }

  _hide(el) {
    if (!el) return;
    el.style.display = 'none';
  }

  /* ---------------- INITIAL VARIANT PICK ---------------- */

  _getVariantFromUrl() {
    try {
      const url = new URL(window.location.href);
      const id = url.searchParams.get('variant');
      if (!id) return null;
      const vid = Number(id);
      return this.variants.find(v => Number(v.id) === vid) || null;
    } catch {
      return null;
    }
  }

  _getCurrentVariantFromHidden() {
    const idInput = this.querySelector('[data-variant-id]');
    if (!idInput) return null;
    const vid = Number(idInput.value);
    if (!vid) return null;
    return this.variants.find(v => Number(v.id) === vid) || null;
  }

  /* ---------------- EVENTS ---------------- */

  onClick(e) {
    const btn = e.target.closest('[data-option-value]');
    if (!btn || !this.contains(btn)) return;

    const item = btn.closest('.variant-picker-item[data-option-index]');
    if (!item) return;

    // set active within this option group
    item.querySelectorAll('[data-option-value].active').forEach(el => {
      el.classList.remove('active');
      el.setAttribute('aria-pressed', 'false');
    });
    btn.classList.add('active');
    btn.setAttribute('aria-pressed', 'true');

    // update label current
    const current = item.querySelector('[data-current]');
    if (current) current.textContent = btn.dataset.optionValue;

    this._syncVariant();
  }

  onChange(e) {
    if (!e.target.matches('[data-option-input]')) return;
    this._syncVariant();
  }

  /* ---------------- OPTIONS -> VARIANT ---------------- */

  _getSelectedOptions() {
    const items = Array.from(this.querySelectorAll('.variant-picker-item[data-option-index]'));
    const maxIdx = items.reduce((m, el) => Math.max(m, parseInt(el.dataset.optionIndex, 10)), -1);
    const options = new Array(maxIdx + 1).fill(null);

    items.forEach(item => {
      const idx = parseInt(item.dataset.optionIndex, 10);

      // select mode
      const sel = item.querySelector('[data-option-input]');
      if (sel) { options[idx] = sel.value; return; }

      // button/image mode
      let active = item.querySelector('[data-option-value].active');
      if (!active) active = item.querySelector('[data-option-value]');
      if (active) options[idx] = active.dataset.optionValue;
    });

    return options;
  }

  _findVariant(options) {
    return this.variants.find(v => {
      if (options[0] && v.option1 !== options[0]) return false;
      if (options[1] && v.option2 !== options[1]) return false;
      if (options[2] && v.option3 !== options[2]) return false;
      return true;
    }) || null;
  }

  _findClosestVariant(options) {
    const [o1, o2, o3] = options;

    let v = this.variants.find(x => (!o1 || x.option1 === o1) && (!o2 || x.option2 === o2) && (!o3 || x.option3 === o3) && x.available);
    if (v) return v;

    v = this.variants.find(x => (!o1 || x.option1 === o1) && (!o2 || x.option2 === o2) && x.available);
    if (v) return v;

    v = this.variants.find(x => (!o1 || x.option1 === o1) && x.available);
    return v || this.variants.find(x => x.available) || this.variants[0] || null;
  }

  /* ---------------- MONEY ---------------- */

  _formatMoney(cents) {
    const fmt = this.getAttribute('data-money-format') || '{{amount}}';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, fmt);
    }

    const value = Number(cents) || 0;

    const formatWithDelimiters = (amountCents, precision, thousands, decimal) => {
      const number = (amountCents / 100).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      const centsPart = parts[1] ? (decimal + parts[1]) : '';
      return dollars + centsPart;
    };

    let token = 'amount';
    const m = fmt.match(/\{\{\s*(amount|amount_no_decimals|amount_with_comma_separator|amount_no_decimals_with_comma_separator)\s*\}\}/);
    if (m && m[1]) token = m[1];

    let formattedNumber = '';
    switch (token) {
      case 'amount':
        formattedNumber = formatWithDelimiters(value, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        formattedNumber = formatWithDelimiters(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        formattedNumber = formatWithDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formattedNumber = formatWithDelimiters(value, 0, '.', ',');
        break;
      default:
        formattedNumber = formatWithDelimiters(value, 2, ',', '.');
    }

    if (!m) return formattedNumber;
    return fmt.replace(m[0], formattedNumber);
  }

  /* ---------------- SYNC VARIANT ---------------- */

  _syncVariant() {
    const options = this._getSelectedOptions();
    let variant = this._findVariant(options);

    if (!variant) {
      variant = this._findClosestVariant(options);
      if (!variant) return;
      this._applyVariantToUI(variant);
    }

    // hidden input inside picker
    const idInput = this.querySelector('[data-variant-id]');
    if (idInput) idInput.value = variant.id;

    // sync hidden input in same root if exists
    const root = this._getRoot();
    const formIdInput = root.querySelector('input[name="id"]');
    if (formIdInput) formIdInput.value = variant.id;

    // update URL
    this._replaceUrlVariant(variant.id);

    // apply all UI updates
    this._applyVariant(variant);

    // dispatch
    this.dispatchEvent(new CustomEvent('variant:change', {
      bubbles: true,
      detail: { variant }
    }));
  }

  _applyVariantToUI(variant) {
    this.querySelectorAll('.variant-picker-item[data-option-index]').forEach(item => {
      const idx = parseInt(item.dataset.optionIndex, 10);
      const value = idx === 0 ? variant.option1 : idx === 1 ? variant.option2 : variant.option3;
      if (!value) return;

      const sel = item.querySelector('[data-option-input]');
      if (sel) {
        sel.value = value;
      } else {
        const btn = item.querySelector(`[data-option-value="${CSS.escape(String(value))}"]`);
        if (btn) {
          item.querySelectorAll('[data-option-value].active').forEach(el => {
            el.classList.remove('active');
            el.setAttribute('aria-pressed', 'false');
          });
          btn.classList.add('active');
          btn.setAttribute('aria-pressed', 'true');

          const current = item.querySelector('[data-current]');
          if (current) current.textContent = value;
        }
      }
    });

    // set hidden id inside picker
    const idInput = this.querySelector('[data-variant-id]');
    if (idInput) idInput.value = variant.id;

    // set hidden id in root if exists
    const root = this._getRoot();
    const formIdInput = root.querySelector('input[name="id"]');
    if (formIdInput) formIdInput.value = variant.id;
  }

  _replaceUrlVariant(id) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', id);
      window.history.replaceState({}, '', url.toString());
    } catch {}
  }

  /* ---------------- APPLY UI UPDATES ---------------- */

  _applyVariant(variant) {
    this._updatePrice(variant);
    this._updateInventory(variant);
    this._updateImage(variant);
    // this._updateCountdown(variant);
  }

  _updatePrice(variant) {
    const root = this._getRoot();
    const wrap = root.querySelector('[data-product-price]');
    if (!wrap) return;

    const priceNew = wrap.querySelector('[data-price-new]');
    const priceOld = wrap.querySelector('[data-price-old]');
    const percentEl = wrap.querySelector('[data-percent-sale]');

    if (priceNew) priceNew.innerHTML = this._formatMoney(variant.price);

    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

    if (onSale) {
      if (priceOld) { priceOld.innerHTML = this._formatMoney(variant.compare_at_price); priceOld.style.display = ''; }
      if (percentEl) {
        const sale = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price);
        percentEl.innerHTML = `${sale}% OFF`;
        const badge = percentEl.closest('.badges-on-sale');
        if (badge) badge.style.display = '';
      }
    } else {
      if (priceOld) priceOld.style.display = 'none';
      if (percentEl) {
        const badge = percentEl.closest('.badges-on-sale');
        if (badge) badge.style.display = 'none';
      }
    }
  }

  _updateInventory(variant) {
    const root = this._getRoot();

    const invWrap =
      root.querySelector('.product-info-progress-sale[data-inventory]') ||
      root.querySelector('[data-inventory]');
    if (!invWrap) return;

    const limit = parseInt(invWrap.dataset.limit || '100', 10);
    const textEl = invWrap.querySelector('[data-inventory-text]');
    const barEl = invWrap.querySelector('[data-progress]') || invWrap.querySelector('.progress-cart .value');

    // ✅ LẤY QTY TỪ LIQUID MAP
    const row = this.inventoryMap ? this.inventoryMap[String(variant.id)] : null;
    const qty = row ? Number(row.qty || 0) : 0;

    // nếu không track inventory thì ẩn (optional)
    const tracked = row ? !!row.tracked : false;

    // Nếu bạn muốn: chỉ show khi track + low stock
    // const show = tracked && qty > 0 && qty <= limit;

    // Nếu bạn muốn: chỉ cần qty (không cần tracked)
    const show = qty > 0 && qty <= limit;

    if (!show) {
      invWrap.style.display = 'none';
      return;
    }

    invWrap.style.display = '';

    const progress = Math.max(0, Math.min(100, Math.round((qty * 100) / limit)));
    if (textEl) textEl.textContent = `Solo quedan ${qty} unidades`;
    if (barEl) {
      barEl.style.width = `${progress}%`;
      barEl.setAttribute('aria-valuenow', String(progress));
    }
  }




  _updateImage(variant) {
    const ex = this.extra[String(variant.id)] || {};
    const mediaId = ex.featured_media_id ? Number(ex.featured_media_id) : 0;
    if (!mediaId) return;

    const swiperEl = document.querySelector('.tf-product-media-main');
    const swiper = swiperEl && swiperEl.swiper ? swiperEl.swiper : null;

    if (swiperEl && swiper) {
      const slides = Array.from(swiperEl.querySelectorAll('.swiper-slide[data-media-id]'));
      const idx = slides.findIndex(s => Number(s.getAttribute('data-media-id')) === mediaId);
      if (idx >= 0) {
        swiper.slideTo(idx, 300);
        return;
      }
    }

    const grid = document.querySelector('.tf-grid-gallery');
    if (grid) {
      const items = Array.from(grid.querySelectorAll('[data-media-id]'));
      const target = items.find(el => Number(el.getAttribute('data-media-id')) === mediaId);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
  }

  _updateCountdown(variant) {
    const root = this._getRoot();
    const cd = root.querySelector('sale-countdown');
    if (!cd || typeof cd.setEnd !== 'function') return;

    const ex = this.extra[String(variant.id)];
    const end = ex && ex.sale_end ? ex.sale_end : null;

    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

    if (onSale && end) {
      this._show(cd);
      cd.setEnd(end);
    } else {
      cd.setEnd(null);
      this._hide(cd);
    }
  }
}

if (!customElements.get('tf-variant-picker')) {
  customElements.define('tf-variant-picker', TfVariantPicker);
}
